import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { apiService } from '../services/api';
import { Notification } from '../types';

const cardShell: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,.04)',
};

type AffiliateStatusFilter = 'pending' | 'approved' | 'rejected' | 'all';

interface PlatformAffiliate {
  id: string;
  email: string;
  firstName: string;
  lastName?: string | null;
  isEmailVerified: boolean;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  programId: string;
  createdAt: string;
  codeCount: number;
}

const PlatformDashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [affiliateFilter, setAffiliateFilter] = useState<AffiliateStatusFilter>('pending');
  const [affiliates, setAffiliates] = useState<PlatformAffiliate[]>([]);
  const [affiliatesLoading, setAffiliatesLoading] = useState(true);
  const [affiliatesError, setAffiliatesError] = useState<string | null>(null);
  const [updatingAffiliateId, setUpdatingAffiliateId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((n: Notification) => !n.is_read));
      } catch {
        setUnreadNotifications([]);
      }
    };
    void load();
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadAffiliates = async () => {
      setAffiliatesLoading(true);
      setAffiliatesError(null);
      try {
        const rows = await apiService.getAffiliatesForAdmin(affiliateFilter);
        if (!cancelled) setAffiliates(rows);
      } catch (error: any) {
        if (!cancelled) {
          setAffiliates([]);
          setAffiliatesError(error?.response?.data?.message || 'Could not load affiliate accounts.');
        }
      } finally {
        if (!cancelled) setAffiliatesLoading(false);
      }
    };
    void loadAffiliates();
    return () => {
      cancelled = true;
    };
  }, [affiliateFilter]);

  const updateAffiliate = async (id: string, status: 'approved' | 'rejected') => {
    setUpdatingAffiliateId(id);
    setAffiliatesError(null);
    try {
      await apiService.setAffiliateApproval(id, status);
      setAffiliates((current) =>
        affiliateFilter === 'all'
          ? current.map((row) => (row.id === id ? { ...row, approvalStatus: status } : row))
          : current.filter((row) => row.id !== id)
      );
    } catch (error: any) {
      setAffiliatesError(error?.response?.data?.message || 'Could not update that affiliate account.');
    } finally {
      setUpdatingAffiliateId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login?type=platform');
    } catch (e) {
      console.error(e);
    }
  };

  const displayName =
    user && [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
      ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
      : user?.email ?? 'Admin';

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton
        showAuthButtons
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/platform')}
      />

      <main className="flex-1 py-8 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto w-full space-y-6">
          <header>
            <p
              className="mb-2 uppercase tracking-wider"
              style={{ fontSize: '12px', fontWeight: 700, color: '#0B4778' }}
            >
              Platform administration
            </p>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome, {displayName}</h1>
            <p className="text-gray-600 text-sm sm:text-base">
              This area is restricted to AACore platform operators. Use the links below to manage public
              content and tools.
            </p>
          </header>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p-5" style={cardShell}>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Blog &amp; resource center</h2>
              <p className="text-sm text-gray-600 mb-4">
                View the public blog, create drafts, and publish updates.
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/blog-admin" className="aacore-btn-outlined justify-center text-center">
                  Open blog
                </Link>
                <Link to="/blog/new" className="aacore-btn-primary justify-center text-center">
                  New post
                </Link>
              </div>
            </div>

            <div className="p-5" style={cardShell}>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Session</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sign out when finished. Platform sign-in is only available at{' '}
                <code className="text-xs bg-gray-100 px-1 rounded">/login?type=platform</code>.
              </p>
              <button type="button" className="aacore-btn-secondary w-full justify-center" onClick={() => void handleLogout()}>
                Sign out
              </button>
            </div>
          </div>

          <section className="p-5" style={cardShell}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Affiliate partners</h2>
                <p className="text-sm text-gray-600">
                  Review partner accounts. Approve an account only after its email is verified.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(['pending', 'approved', 'rejected', 'all'] as AffiliateStatusFilter[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setAffiliateFilter(status)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium border capitalize ${
                      affiliateFilter === status
                        ? 'bg-[#0B4778] text-white border-[#0B4778]'
                        : 'bg-white text-gray-700 border-gray-300'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {affiliatesError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">{affiliatesError}</p>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-4 font-semibold">Partner</th>
                    <th className="pb-2 pr-4 font-semibold">Program</th>
                    <th className="pb-2 pr-4 font-semibold">Email</th>
                    <th className="pb-2 pr-4 font-semibold">Status</th>
                    <th className="pb-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliatesLoading ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-gray-500">Loading affiliate accounts…</td>
                    </tr>
                  ) : affiliates.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-gray-500">No affiliate accounts in this view.</td>
                    </tr>
                  ) : (
                    affiliates.map((affiliate) => (
                      <tr key={affiliate.id} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-gray-900">
                            {affiliate.firstName} {affiliate.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(affiliate.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </td>
                        <td className="py-3 pr-4 font-mono text-xs text-gray-700">{affiliate.programId}</td>
                        <td className="py-3 pr-4">
                          <div className="text-gray-900">{affiliate.email}</div>
                          <div className="text-xs text-gray-500">
                            {affiliate.isEmailVerified ? 'Email verified' : 'Email not verified'}
                            {affiliate.codeCount > 0 ? ` · ${affiliate.codeCount} codes` : ''}
                          </div>
                        </td>
                        <td className="py-3 pr-4 capitalize text-gray-700">{affiliate.approvalStatus}</td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {affiliate.isEmailVerified && affiliate.approvalStatus !== 'approved' && (
                              <button
                                type="button"
                                disabled={updatingAffiliateId === affiliate.id}
                                onClick={() => void updateAffiliate(affiliate.id, 'approved')}
                                className="text-sm font-medium text-[#0B4778] disabled:opacity-50"
                              >
                                Approve
                              </button>
                            )}
                            {!affiliate.isEmailVerified && (
                              <span className="text-xs text-gray-500">Waiting on email</span>
                            )}
                            {affiliate.approvalStatus !== 'rejected' && (
                              <button
                                type="button"
                                disabled={updatingAffiliateId === affiliate.id}
                                onClick={() => void updateAffiliate(affiliate.id, 'rejected')}
                                className="text-sm font-medium text-red-700 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PlatformDashboardPage;
